/**
 * personalization-quiz.js  v2
 * Viori Personalizer — Phase 2: Quiz Engine
 *
 * Always shows all 5 questions. Pre-fills answers from VioriState
 * (set via homepage interactions). Back + Next navigation on every step.
 * 5-dot progress indicator. Pet / Kids on Q1 → instant redirect to results.
 * On completion, infers dryScalpCondition and routes to personalized collection.
 *
 * Questions (spec Section 7a):
 *   Q1   whoIsThisFor   — single-select (pet/kids → instant redirect)
 *   Q2   hairGoals      — multi-select
 *   Q3   hairType       — single-select
 *   Q3.5 hairConcerns   — multi-select
 *   Q4   hairLookALike  — visual selector
 *
 * Depends on: personalization-schema.js, personalization-state.js
 * Spec refs: Section 7, 7a, 7b, 7c
 */

(function (root) {
  'use strict';

  var VS = null; // set on init

  // ── Question Definitions ───────────────────────────────────────────────────
  var QUESTIONS = [
    {
      id: 'q1',
      stateKey: 'whoIsThisFor',
      alwaysShow: true,
      type: 'single',        // logic: single-select (one answer saved to state)
      displayType: 'multi',  // display: list layout (same CSS as Q2/Q4)
      heading: 'Who are you shopping for?',
      subheading: 'We\'ll personalize our recommendations.',
      options: [
        { value: 'myself',  label: 'Myself',        emoji: '👩' },
        { value: 'mens',    label: 'Men\'s Hair',    emoji: '👨' },
        { value: 'kids',    label: 'My Kids',        emoji: '👧' },
        { value: 'pet',     label: 'My Pet',         emoji: '🐾' },
        { value: 'gift',    label: 'It\'s a Gift',   emoji: '🎁' },
      ],
    },
    {
      id: 'q2',
      stateKey: 'hairGoals',
      alwaysShow: false,
      type: 'multi',
      heading: 'What are your hair goals?',
      subheading: 'Select all that apply.',
      whyWeAsk: 'Helps us match you to the formula designed for your specific goal.',
      options: [
        { value: 'hairGrowth',          label: 'Hair Growth',                icon: 'growth' },
        { value: 'scalpHydration',       label: 'Scalp Hydration',            icon: 'drop' },
        { value: 'repairDryDamaged',     label: 'Repair Dry or Damaged Hair', icon: 'repair' },
        { value: 'improveShineSoftness', label: 'Improve Shine & Softness',   icon: 'shine' },
        { value: 'balanceScalpOil',      label: 'Balance Scalp Oil',          icon: 'balance' },
        { value: 'sensitiveScalpCare',   label: 'Sensitive Scalp Care',       icon: 'sensitive' },
      ],
    },
    {
      id: 'q3',
      stateKey: 'hairType',
      alwaysShow: false,
      type: 'single',
      displayType: 'multi',  // display: list layout (options have sublabels)
      heading: 'What is your hair type?',
      subheading: 'Based on how often you wash.',
      whyWeAsk: 'Different washing frequencies signal different scalp oil levels — key to picking the right formula.',
      options: [
        { value: 'oily',   label: 'Oily Hair',   sublabel: 'I wash every 1–2 days' },
        { value: 'normal', label: 'Normal Hair',  sublabel: 'I wash 3–4 times a week' },
        { value: 'dry',    label: 'Dry Hair',     sublabel: 'I wash 1–2 times a week or less' },
      ],
    },
    {
      id: 'q4',
      stateKey: 'hairConcerns',
      alwaysShow: false,
      type: 'multi',
      heading: 'Any hair concerns?',
      subheading: 'Select all that apply.',
      whyWeAsk: 'Lets us fine-tune recommendations and show you reviews from people with the same concerns.',
      options: [
        { value: 'thinning',        label: 'Thinning or hair loss' },
        { value: 'dandruff',        label: 'Dandruff or flaky scalp' },
        { value: 'sensitiveScalp',  label: 'Sensitive scalp (reacts to products)' },
        { value: 'colorTreated',    label: 'Blonde, color-treated, or highlighted' },
        { value: 'flatHair',        label: 'Flat hair — need volume' },
        { value: 'damagedFrizzy',   label: 'Damaged, frizzy, or brittle' },
        { value: 'noneHealthyHair', label: 'None of these — just want healthy hair' },
      ],
    },
    {
      id: 'q5',
      stateKey: 'hairLookALike',
      alwaysShow: true,
      type: 'visual',
      heading: 'Which hair type most resembles yours?',
      subheading: 'Choose the closest match.',
      options: [
        { value: 'naturallyBlonde', label: 'Naturally Blonde', imgAlt: 'Naturally blonde hair' },
        { value: 'blonde',          label: 'Blonde Hair',       imgAlt: 'Blonde hair' },
        { value: 'brunette',        label: 'Brunette Hair',     imgAlt: 'Brunette hair' },
        { value: 'blackHair',       label: 'Black Hair',        imgAlt: 'Black hair' },
        { value: 'redHair',         label: 'Red Hair',          imgAlt: 'Red hair' },
        { value: 'coloredHair',     label: 'Colored Hair',      imgAlt: 'Colored hair' },
        { value: 'brownCurly',      label: 'Brown Curly Hair',  imgAlt: 'Brown curly hair' },
        { value: 'blackCurly',      label: 'Black Curly Hair',  imgAlt: 'Black curly hair' },
      ],
    },
  ];

  // ── Dry Scalp Condition Inference ─────────────────────────────────────────
  function _inferDryScalp() {
    var hairType     = VS.getState('hairType');
    var concerns     = VS.getState('hairConcerns') || [];
    var scalpSignals = ['dandruff', 'sensitiveScalp'];
    if (hairType === 'dry' && scalpSignals.some(function (c) { return concerns.indexOf(c) !== -1; })) {
      VS.setState('hairType', 'dryScalpCondition');
    }
  }

  // ── Queue — always all 5 questions ────────────────────────────────────────
  function _buildQueue() {
    return QUESTIONS.slice();
  }

  // ── State vars ─────────────────────────────────────────────────────────────
  var _queue        = [];
  var _currentIndex = 0;
  var _container    = null;
  var _onComplete   = null;
  var _resultsUrl   = '/collections/quiz-results';

  // ── Display overrides — merge schema text into QUESTIONS display fields ─────
  // Reads from #vp-quiz-display JSON injected by cust-quiz.liquid.
  // Only heading and subheading are overridable — stateKey, values, and logic
  // stay hardcoded in QUESTIONS and are never touched by this function.
  function _applyDisplayOverrides() {
    try {
      var el = document.getElementById('vp-quiz-display');
      if (!el) return;
      var data = JSON.parse(el.textContent);
      if (!data || !data.questions) return;
      QUESTIONS.forEach(function (q) {
        var ov = data.questions[q.id];
        if (!ov) return;
        // null  = field was explicitly cleared → hide (set to '')
        // string = field has content → use it
        // undefined = field not in JSON → keep JS default
        if (ov.heading    !== undefined) q.heading    = ov.heading    ? ov.heading.trim()    : '';
        if (ov.subheading !== undefined) q.subheading = ov.subheading ? ov.subheading.trim() : '';
      });
    } catch (e) {}
  }

  // ── Label overrides — merge schema option labels into QUESTIONS ──────────────
  // Reads from #vp-quiz-labels JSON injected by cust-quiz.liquid.
  // Only label and sublabel (display text) are overridable.
  // Option values (e.g. 'oily', 'hairGrowth') are never touched.
  function _applyLabelOverrides() {
    try {
      var el = document.getElementById('vp-quiz-labels');
      if (!el) return;
      var data = JSON.parse(el.textContent);
      if (!data) return;
      QUESTIONS.forEach(function (q) {
        var qLabels = data[q.id];
        if (!qLabels) return;
        q.options.forEach(function (opt) {
          var ov = qLabels[opt.value];
          if (!ov) return;
          if (typeof ov === 'string') {
            if (ov.trim()) opt.label = ov.trim();
          } else {
            if (ov.label    && ov.label.trim())    opt.label    = ov.label.trim();
            if (ov.sublabel && ov.sublabel.trim()) opt.sublabel = ov.sublabel.trim();
            // emoji can be intentionally blank (to hide it), so only skip if undefined
            if (ov.emoji !== undefined)            opt.emoji    = ov.emoji;
          }
        });
      });
    } catch (e) {}
  }

  // ── Init ───────────────────────────────────────────────────────────────────
  function init(containerEl, options) {
    VS = root.VioriState;
    if (!VS) { console.error('[VioriQuiz] VioriState not loaded'); return; }

    _container  = containerEl;
    _onComplete = (options && options.onComplete) || null;
    _resultsUrl = (options && options.resultsUrl) || '/collections/quiz-results';

    if (root.VioriEvents) root.VioriEvents.trackQuizStart();

    _applyDisplayOverrides(); // merge schema heading/subheading text
    _applyLabelOverrides();   // merge schema option label text
    _queue        = _buildQueue();
    _currentIndex = 0;

    _render();
  }

  // ── Redirect URL — preserves preview_theme_id + appends state params ────────
  // ?ht= (hairType) and ?wf= (whoIsThisFor) are read by the collection page as
  // a fallback when localStorage / sessionStorage is unavailable (private mode,
  // consent not granted, storage cleared, cross-device). Spec §3 state fallback.
  function _buildRedirectUrl(base) {
    var url = base || _resultsUrl;
    var params = [];

    // Dev theme preview passthrough
    try {
      var previewId = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (previewId) params.push('preview_theme_id=' + encodeURIComponent(previewId));
    } catch (e) {}

    // State params — only append if value is set
    if (VS) {
      var ht = VS.getState('hairType');
      var wf = VS.getState('whoIsThisFor');
      if (ht) params.push('ht=' + encodeURIComponent(ht));
      if (wf) params.push('wf=' + encodeURIComponent(wf));
    }

    if (params.length) {
      url += (url.indexOf('?') >= 0 ? '&' : '?') + params.join('&');
    }
    return url;
  }

  // ── answer(stateKey, value) ────────────────────────────────────────────────
  function answer(stateKey, value) {
    if (!VS) return;
    var q = _queue[_currentIndex];
    if (!q) return;

    if (root.VioriEvents) root.VioriEvents.trackQuizStep(_currentIndex + 1, stateKey, value);

    // Pet / Kids → set defaults and redirect immediately (no further quiz questions)
    if (stateKey === 'whoIsThisFor' && (value === 'pet' || value === 'kids')) {
      VS.setState('whoIsThisFor', value);
      if (!VS.getState('hairType')) VS.setState('hairType', 'normal');
      VS.setState('quizComplete', true);
      if (root.VioriEvents) root.VioriEvents.trackQuizComplete();
      window.location.href = _buildRedirectUrl();
      return;
    }

    var saved = VS.setState(stateKey, value);
    if (!saved) return;

    // Update selection state in UI without rebuilding DOM
    _updateSelectionsAll(q);
  }

  // ── advanceMulti — kept for backwards compat ───────────────────────────────
  function advanceMulti() {
    _advance();
  }

  function _advance() {
    _currentIndex++;
    if (_currentIndex >= _queue.length) {
      _complete();
    } else {
      _render();
    }
  }

  function _back() {
    if (_currentIndex <= 0) return;
    _currentIndex--;
    _render();
  }

  function _complete() {
    _inferDryScalp();
    VS.setState('quizComplete', true);
    if (root.VioriEvents) root.VioriEvents.trackQuizComplete();
    if (_onComplete) {
      _onComplete(VS.getState());
    } else {
      window.location.href = _buildRedirectUrl();
    }
  }

  // ── Rendering ──────────────────────────────────────────────────────────────
  function _render() {
    if (!_container) return;
    var q = _queue[_currentIndex];
    if (!q) return;

    _container.innerHTML = _buildQuestionHTML(q);
    _loadImages();
    _attachListeners(q);

    try { _container.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
  }

  function _buildQuestionHTML(q) {
    var activeDots = _currentIndex; // dots = number of answered questions
    var isLastQ    = _currentIndex >= _queue.length - 1;

    var html = '<div class="vp-quiz__step" data-q-id="' + q.id + '">';

    // ── Progress dots ──────────────────────────────────────────────────────
    html += '<div class="vp-quiz__dots" role="progressbar"';
    html += ' aria-valuenow="' + activeDots + '" aria-valuemin="0" aria-valuemax="5"';
    html += ' aria-label="Question ' + (_currentIndex + 1) + ' of 5">';
    for (var d = 0; d < 5; d++) {
      html += '<span class="vp-quiz__dot' + (d < activeDots ? ' is-active' : '') + '"></span>';
    }
    html += '</div>';

    // ── Heading ────────────────────────────────────────────────────────────
    html += '<div class="vp-quiz__header">';
    if (q.heading)    html += '<h2 class="vp-quiz__heading">'    + _esc(q.heading)    + '</h2>';
    if (q.subheading) html += '<p class="vp-quiz__subheading">'  + _esc(q.subheading) + '</p>';
    if (q.whyWeAsk) {
      html += '<button class="vp-quiz__why-btn" type="button" aria-expanded="false">Why we ask</button>';
      html += '<div class="vp-quiz__why-body" hidden>' + _esc(q.whyWeAsk) + '</div>';
    }
    html += '</div>';

    // ── Options ────────────────────────────────────────────────────────────
    // displayType overrides the CSS layout class without affecting click logic
    var optClass = q.displayType || q.type;
    html += '<div class="vp-quiz__options vp-quiz__options--' + optClass + '">';
    q.options.forEach(function (opt) {
      var selected = _isSelected(q, opt.value);
      html += '<button class="vp-quiz__option' + (selected ? ' is-selected' : '') + '"';
      html += ' type="button" data-value="' + _esc(opt.value) + '"';
      html += ' data-state-key="' + _esc(q.stateKey) + '"';
      if (q.type === 'visual') {
        html += ' data-img-key="' + opt.value + '"';
      }
      html += ' aria-pressed="' + selected + '">';
      if (q.type === 'visual') {
        // Image placeholder — src populated by _loadImages() after render
        html += '<img class="vp-quiz__option-img" src="" alt="' + _esc(opt.imgAlt || opt.label) + '" loading="lazy">';
      } else if (q.type === 'single' && opt.emoji) {
        html += '<span class="vp-quiz__option-emoji" aria-hidden="true">' + opt.emoji + '</span>';
      }
      html += '<span class="vp-quiz__option-label">' + _esc(opt.label) + '</span>';
      if (opt.sublabel) html += '<span class="vp-quiz__option-sublabel">' + _esc(opt.sublabel) + '</span>';
      html += '</button>';
    });
    html += '</div>';

    // ── Back / Next nav row ────────────────────────────────────────────────
    html += '<div class="vp-quiz__nav">';
    if (_currentIndex > 0) {
      html += '<button class="vp-quiz__nav-back" type="button" id="vp-quiz-back">&#8592; Back</button>';
    } else {
      html += '<span class="vp-quiz__nav-spacer"></span>';
    }
    var nextLabel = isLastQ ? 'See My Results' : 'Next &#8594;';
    html += '<button class="vp-quiz__nav-next" type="button" id="vp-quiz-next">' + nextLabel + '</button>';
    html += '</div>';

    // ── Skip link — every page ─────────────────────────────────────────────
    // Hard destination: /collections/all. A user clicking this is explicitly
    // declining personalization, so we do NOT append quiz state params (ht,
    // wf) that _buildRedirectUrl() would add — we just pass through the dev
    // preview_theme_id so the preview session isn't lost.
    var skipUrl = '/collections/all';
    try {
      var _previewId = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (_previewId) skipUrl += '?preview_theme_id=' + encodeURIComponent(_previewId);
    } catch (e) { /* ignore */ }
    html += '<div class="vp-quiz__skip">';
    html += '<a href="' + skipUrl + '" class="vp-quiz__skip-link">Skip to product collection page</a>';
    html += '</div>';

    html += '</div>'; // .vp-quiz__step
    return html;
  }

  function _isSelected(q, value) {
    var stored = VS.getState(q.stateKey);
    if (Array.isArray(stored)) return stored.indexOf(value) !== -1;
    return stored === value;
  }

  // Updates selection classes on all option buttons without rebuilding DOM
  function _updateSelectionsAll(q) {
    if (!_container) return;
    var buttons = _container.querySelectorAll('.vp-quiz__option');
    var stored  = VS.getState(q.stateKey);
    buttons.forEach(function (btn) {
      var val   = btn.getAttribute('data-value');
      var isNow = Array.isArray(stored) ? stored.indexOf(val) !== -1 : stored === val;
      btn.classList.toggle('is-selected', isNow);
      btn.setAttribute('aria-pressed', String(isNow));
    });
  }

  function _showValidationError(containerEl) {
    var existing = containerEl.querySelector('.vp-quiz__validation-msg');
    if (existing) return; // already showing
    var msg = document.createElement('p');
    msg.className = 'vp-quiz__validation-msg';
    msg.textContent = 'Please select an answer to continue.';
    var nav = containerEl.querySelector('.vp-quiz__nav');
    if (nav) nav.insertAdjacentElement('beforebegin', msg);
    setTimeout(function () { if (msg.parentNode) msg.parentNode.removeChild(msg); }, 3000);
  }

  function _attachListeners(q) {
    if (!_container) return;

    // Option clicks
    var buttons = _container.querySelectorAll('.vp-quiz__option');
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var val      = btn.getAttribute('data-value');
        var stateKey = btn.getAttribute('data-state-key');
        if (q.type === 'multi') {
          // Toggle in array
          var current = VS.getState(stateKey) || [];
          var idx     = current.indexOf(val);
          var updated;
          if (val === 'noneHealthyHair') {
            updated = idx === -1 ? [val] : [];
          } else {
            var filtered = current.filter(function (v) { return v !== 'noneHealthyHair'; });
            updated = idx !== -1
              ? filtered.filter(function (v) { return v !== val; })
              : filtered.concat(val);
          }
          VS.setState(stateKey, updated);
          _updateSelectionsAll(q);
        } else {
          // Single / visual — save state and update UI
          // Visual: clicking the already-selected option deselects it
          if (q.type === 'visual' && VS.getState(stateKey) === val) {
            VS.setState(stateKey, null);
            _updateSelectionsAll(q);
          } else {
            // (pet/kids will redirect immediately inside answer())
            answer(stateKey, val);
          }
        }
      });
    });

    // Back button
    var backBtn = _container.querySelector('#vp-quiz-back');
    if (backBtn) {
      backBtn.addEventListener('click', function () { _back(); });
    }

    // Next button — validates selection before advancing
    var nextBtn = _container.querySelector('#vp-quiz-next');
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        var stored = VS.getState(q.stateKey);
        var hasAnswer = Array.isArray(stored) ? stored.length > 0 : !!stored;
        if (!hasAnswer) {
          _showValidationError(_container);
          return;
        }
        _advance();
      });
    }

    // Why-we-ask toggle
    var whyBtn  = _container.querySelector('.vp-quiz__why-btn');
    var whyBody = _container.querySelector('.vp-quiz__why-body');
    if (whyBtn && whyBody) {
      whyBtn.addEventListener('click', function () {
        var expanded = whyBtn.getAttribute('aria-expanded') === 'true';
        whyBtn.setAttribute('aria-expanded', String(!expanded));
        whyBody.hidden = expanded;
      });
    }
  }

  // ── Image loader — populates visual option <img> src from Liquid JSON ─────
  // Reads #vp-quiz-hair-images injected by cust-quiz.liquid. Called after each
  // _render() so Q5 images are set regardless of which question was showing at
  // page load. No-ops silently on non-visual questions (no .vp-quiz__option-img).
  function _loadImages() {
    if (!_container) return;
    var el = document.getElementById('vp-quiz-hair-images');
    if (!el) return;
    var imgMap;
    try { imgMap = JSON.parse(el.textContent); } catch (e) { return; }
    if (!imgMap) return;
    _container.querySelectorAll('.vp-quiz__option-img').forEach(function (img) {
      var btn = img.closest('[data-img-key]');
      var key = btn ? btn.getAttribute('data-img-key') : null;
      if (key && imgMap[key]) img.src = imgMap[key];
    });
  }

  // ── Utilities ──────────────────────────────────────────────────────────────
  function _esc(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#x27;');
  }

  // ── Public Interface ───────────────────────────────────────────────────────
  root.VioriQuiz = {
    init:         init,
    answer:       answer,
    advanceMulti: advanceMulti,
    back:         _back,
    getQueue:     function () { return _queue.slice(); },
    getCurrentQ:  function () { return _queue[_currentIndex] || null; },
    getProgress:  function () { return _currentIndex; },
    questions:    QUESTIONS,
  };

}(window));
